import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

import { Answer, CartItem, ServiceDetailAnswer } from "@/api/types";

// Keyed by patientId → cartItemId → questionId → answer
interface ServiceDetailAnswersState {
  answers: Record<string, Record<number, Record<number, Answer>>>;
}

interface ServiceDetailAnswersActions {
  setAnswer: (patientId: string, cartItemId: number, questionId: number, answer: Answer) => void;
  getAnswers: (patientId: string, cartItemId: number) => Record<number, Answer>;
  clearAnswers: (patientId: string, cartItemId: number) => void;
  clearPatientAnswers: (patientId: string) => void;
  clearAll: () => void;
  getAnswersForCheckout: (patientId: string, cartItems: CartItem[]) => Record<string, ServiceDetailAnswer[]>;
}

export type ServiceDetailAnswersStore = ServiceDetailAnswersState & ServiceDetailAnswersActions;

const initialState: ServiceDetailAnswersState = {
  answers: {},
};

export const useServiceDetailAnswersStore = create<ServiceDetailAnswersStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        setAnswer: (patientId, cartItemId, questionId, answer) => {
          set(
            (state) => {
              const patientAnswers = state.answers[patientId] || {};
              const itemAnswers = patientAnswers[cartItemId] || {};
              return {
                ...state,
                answers: {
                  ...state.answers,
                  [patientId]: {
                    ...patientAnswers,
                    [cartItemId]: {
                      ...itemAnswers,
                      [questionId]: answer,
                    },
                  },
                },
              };
            },
            undefined,
            "serviceDetailAnswers/setAnswer",
          );
        },

        getAnswers: (patientId, cartItemId) => {
          const state = get();
          return state.answers[patientId]?.[cartItemId] || {};
        },

        clearAnswers: (patientId, cartItemId) => {
          set(
            (state) => {
              const patientAnswers = state.answers[patientId];
              if (!patientAnswers) return state;

              const remainingItems = Object.fromEntries(
                Object.entries(patientAnswers).filter(([key]) => Number(key) !== cartItemId),
              );
              return {
                ...state,
                answers: {
                  ...state.answers,
                  [patientId]: remainingItems,
                },
              };
            },
            undefined,
            "serviceDetailAnswers/clearAnswers",
          );
        },

        clearPatientAnswers: (patientId) => {
          set(
            (state) => {
              const remainingPatients = Object.fromEntries(
                Object.entries(state.answers).filter(([key]) => key !== patientId),
              );
              return {
                ...state,
                answers: remainingPatients,
              };
            },
            undefined,
            "serviceDetailAnswers/clearPatientAnswers",
          );
        },

        clearAll: () => {
          set(
            {
              ...initialState,
            },
            undefined,
            "serviceDetailAnswers/clearAll",
          );
        },

        getAnswersForCheckout: (patientId, cartItems) => {
          const state = get();
          const patientAnswers = state.answers[patientId] || {};
          const checkoutAnswers: Record<string, ServiceDetailAnswer[]> = {};

          cartItems.forEach((item) => {
            const cartItemId = item.cart_item_id ?? item.item_id;
            if (!cartItemId) return;

            const itemAnswers = patientAnswers[cartItemId] || {};
            const serviceDetailAnswers: ServiceDetailAnswer[] = Object.entries(itemAnswers).map(
              ([questionId, answer]) => ({
                service_question_id: Number.parseInt(questionId, 10),
                answer: Array.isArray(answer) ? answer.join(",") : answer,
              }),
            );

            if (serviceDetailAnswers.length > 0) {
              checkoutAnswers[String(cartItemId)] = serviceDetailAnswers;
            }
          });

          return checkoutAnswers;
        },
      }),
      {
        name: "service-detail-answers-store",
        version: 1,
        // v0 keyed answers by serviceId; v1 keys by cartItemId. Reset any
        // persisted v0 state so stale serviceId buckets can't collide with
        // live cart_item_id lookups.
        migrate: () => ({ answers: {} }),
      },
    ),
    {
      name: "service-detail-answers-store",
    },
  ),
);
